import React, { useEffect, useState } from 'react';
import { FiCheckCircle, FiPlay, FiSquare } from 'react-icons/fi';
import { getScheduleStatus, enableSchedule, disableSchedule, type ScheduleStatus, type ScheduleConfig } from '../ivansMonitorService';

function describeConfig(config: ScheduleConfig | undefined): string {
  if (!config) return 'Running';
  if (config.mode === 'interval') return `Running every ${config.minutes} minute(s)`;
  const hh = String(config.hour).padStart(2, '0');
  const mm = String(config.minute).padStart(2, '0');
  return `Running daily at ${hh}:${mm}`;
}

const ScheduleTab: React.FC = () => {
  const [status, setStatus] = useState<ScheduleStatus | null>(null);
  const [mode, setMode] = useState<'interval' | 'daily'>('interval');
  const [minutes, setMinutes] = useState(60);
  const [time, setTime] = useState('03:00');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    const result = await getScheduleStatus().catch(() => ({ enabled: false, supported: false } as ScheduleStatus));
    setStatus(result);
    // Pre-fill the form with whatever's actually running, so reopening this
    // tab doesn't reset back to defaults and silently change the schedule
    // if you just hit "Update Schedule" without touching anything.
    if (result.config?.mode === 'interval') {
      setMode('interval');
      setMinutes(result.config.minutes);
    } else if (result.config?.mode === 'daily') {
      setMode('daily');
      setTime(`${String(result.config.hour).padStart(2, '0')}:${String(result.config.minute).padStart(2, '0')}`);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleEnable = async () => {
    setBusy(true);
    setError(null);
    try {
      const config: ScheduleConfig =
        mode === 'interval'
          ? { mode: 'interval', minutes }
          : (() => {
              const [hour, minute] = time.split(':').map(Number);
              return { mode: 'daily', hour, minute };
            })();
      const result = await enableSchedule(config);
      setStatus(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to enable scheduling.');
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await disableSchedule();
      setStatus(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disable scheduling.');
    } finally {
      setBusy(false);
    }
  };

  if (!status) return <div className="ca-empty">Loading…</div>;

  return (
    <div className="ivm-section">
      <div className="ivm-section__header">
        <div className="ivm-section__title">Schedule</div>
      </div>

      {!status.supported && (
        <div className="ca-run__status ca-run__status--error">Scheduling isn't supported on this machine's OS.</div>
      )}

      {status.supported && (
        <>
          <div className="ivm-schedule-status">
            {status.enabled ? (
              <span className="ivm-pill ivm-pill--clean"><FiCheckCircle size={11} /> {describeConfig(status.config)}</span>
            ) : (
              <span className="ivm-pill ivm-pill--unconfirmed">Not scheduled</span>
            )}
          </div>

          <div className="ivm-schedule-mode" role="radiogroup" aria-label="Schedule mode">
            <label className="ivm-schedule-mode__option">
              <input type="radio" name="schedule-mode" checked={mode === 'interval'} onChange={() => setMode('interval')} />
              Repeat every few minutes
            </label>
            <label className="ivm-schedule-mode__option">
              <input type="radio" name="schedule-mode" checked={mode === 'daily'} onChange={() => setMode('daily')} />
              Once a day at a set time
            </label>
          </div>

          <div className="ca-run__grid">
            {mode === 'interval' ? (
              <label className="ca-run__field">
                <span>How many minutes between checks</span>
                <input className="ca-run__input" type="number" min={1} value={minutes} onChange={(e) => setMinutes(Number(e.target.value))} />
              </label>
            ) : (
              <label className="ca-run__field">
                <span>Time (runs once a day, machine's local time)</span>
                <input className="ca-run__input" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
              </label>
            )}
          </div>

          <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
            <button type="button" className="ca-run__btn" disabled={busy} onClick={handleEnable}>
              <FiPlay size={13} /> {status.enabled ? 'Update Schedule' : 'Enable Schedule'}
            </button>
            {status.enabled && (
              <button type="button" className="ivm-remove-btn" disabled={busy} onClick={handleDisable}>
                <FiSquare size={12} /> Disable
              </button>
            )}
          </div>
        </>
      )}

      {error && <div className="ca-run__status ca-run__status--error" style={{ marginTop: 'var(--space-2)' }}>{error}</div>}
    </div>
  );
};

export default ScheduleTab;
